import { Driver } from './../drivers/drivers.type'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  EquipmentType,
  Tractor,
  Trailer,
  TripsError,
} from '../../pages/trip/trip.types'
import {
  CreateTripsFixture,
  DriverFixture,
  OrderPickupFixture,
  tractorsFixture,
  trailersFixture,
} from './trips.fixtures'
import { EquipmentResponse } from '../equipments/equipments.types'
import {
  CreateTripPayload,
  OrderEvent,
  SessionCreatedOrderEvent,
  TripPayload,
  TripResponse,
} from './trips.types'
import {
  getEquipmentHookIds,
  getMaxSequenceId,
  prepareTripTasks,
  removeEquipmentTrailerTasks,
  tripResponseToCreatePayload,
} from './trips.utils'

export interface TripsState {
  equipments: EquipmentResponse[]
  tripsPayload: CreateTripPayload
  editTripOrderEventIds: number[]
  orderId: number
  orderEventId: number
  /** Drop/hook order events created in this trip session; cleaned up on remove/cancel. */
  sessionCreatedOrderEvents: SessionCreatedOrderEvent[]
  // deliveryToDrop: CreateTripPayload
  firstDriver: Driver
  secondDriver: Driver
  // rowsNew: TripDetailNew[]
  // rows: TripDetail[]
  tractors: Tractor[]
  trailers: Trailer[]
  orders: OrderEvent[]
  error: TripsError
  selectedTrip: TripResponse | undefined
  tripCreateCompletion: {
    isComplete: boolean
    driverName: string
  }
}

const initialState: TripsState = {
  equipments: [],
  tripsPayload: CreateTripsFixture,
  editTripOrderEventIds: [],
  orderId: 0,
  orderEventId: 0,
  sessionCreatedOrderEvents: [],
  // deliveryToDrop: CreateTripsFixture,
  firstDriver: DriverFixture,
  secondDriver: DriverFixture,
  // rowsNew: [],
  // rows: [],
  tractors: tractorsFixture,
  trailers: trailersFixture,
  orders: [OrderPickupFixture],
  error: {
    open: false,
    message: '',
    variant: 'info',
  },
  selectedTrip: undefined,
  tripCreateCompletion: {
    isComplete: false,
    driverName: '',
  },
}

export const tripsSlice = createSlice({
  name: 'trip',
  initialState,
  reducers: {
    addFirstDriver: (state, action: PayloadAction<Driver>) => {
      state.firstDriver = action.payload
    },
    addSecondDriver: (state, action: PayloadAction<Driver>) => {
      state.secondDriver = action.payload
    },
    addDrivers: (state, action: PayloadAction<Driver[]>) => {
      state.tripsPayload.firstDriverName = `${action.payload[0].firstName} ${action.payload[0].lastName}`
      state.tripsPayload.firstDriverId = action.payload[0].id
      state.tripsPayload.secondDriverName = `${action.payload[1].firstName} ${action.payload[1].lastName}`
      state.tripsPayload.secondDriverId = action.payload[1].id
    },
    addEquipments: (state, action: PayloadAction<TripPayload[]>) => {
      state.tripsPayload.tripTasks = prepareTripTasks([
        ...state.tripsPayload.tripTasks,
        ...action.payload,
      ])
    },
    removeTractor: (state) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        state.tripsPayload.tripTasks.filter(
          (task) =>
            task.type !== 'HOOK_TRACTOR' && task.type !== 'DROP_TRACTOR'
        )
      )
    },
    removeTrailer: (state) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        removeEquipmentTrailerTasks(state.tripsPayload.tripTasks)
      )
    },
    removeTrailerById: (
      state,
      action: PayloadAction<{ trailerId: string }>
    ) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        removeEquipmentTrailerTasks(
          state.tripsPayload.tripTasks,
          action.payload.trailerId
        )
      )
    },
    replaceTrailerById: (
      state,
      action: PayloadAction<{
        trailerId: string
        equipment: EquipmentResponse
      }>
    ) => {
      const { trailerId, equipment } = action.payload
      const replacementTrailerId = String(equipment.equipmentId)

      state.tripsPayload.tripTasks = prepareTripTasks(
        state.tripsPayload.tripTasks.map((task) => {
          if (task.trailerId !== trailerId) return task

          const isEquipmentTrailerTask =
            task.orderEventId == null &&
            task.sourceOrderPkId == null &&
            !task.orderId &&
            (task.type === 'HOOK_TRAILER' || task.type === 'DROP_TRAILER')

          return {
            ...task,
            trailerId: replacementTrailerId,
            ...(isEquipmentTrailerTask
              ? {
                  name: equipment.unitNumber,
                  address: equipment.address,
                }
              : {}),
          }
        })
      )
    },
    removeTaskById: (
      state,
      action: PayloadAction<{ orderEventId: number; type?: TripPayload['type'] }>
    ) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        state.tripsPayload.tripTasks.filter(
          (task) =>
            !(
              task.orderEventId === action.payload.orderEventId &&
              (action.payload.type == null || task.type === action.payload.type)
            )
        )
      )
    },
    removeOrderTasks: (
      state,
      action: PayloadAction<{ orderId: string }>
    ) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        state.tripsPayload.tripTasks.filter(
          (task) =>
            task.orderId !== action.payload.orderId ||
            (task.type !== 'PICKUP' && task.type !== 'DELIVERY')
        )
      )
    },
    addDropTrailerTask: (
      state,
      action: PayloadAction<{
        address: string
        orderId: string
        sourceOrderPkId: number
        removeDeliveryOrderEventId?: number
      }>
    ) => {
      const {
        address,
        orderId,
        sourceOrderPkId,
        removeDeliveryOrderEventId,
      } = action.payload

      const deliveryTask = removeDeliveryOrderEventId
        ? state.tripsPayload.tripTasks.find(
            (task) => task.orderEventId === removeDeliveryOrderEventId
          )
        : undefined

      let tripTasks = state.tripsPayload.tripTasks.filter(
        (task) =>
          !(
            task.type === 'DROP_TRAILER' &&
            task.sourceOrderPkId === sourceOrderPkId &&
            task.orderEventId == null
          )
      )

      if (removeDeliveryOrderEventId) {
        tripTasks = tripTasks.filter(
          (task) => task.orderEventId !== removeDeliveryOrderEventId
        )
      }

      const { tractorId, trailerId: currentTrailerId } =
        getEquipmentHookIds(tripTasks)
      const trailerId = deliveryTask?.trailerId ?? currentTrailerId

      const sequenceId = deliveryTask
        ? deliveryTask.sequenceId
        : getMaxSequenceId(tripTasks) + 10

      state.tripsPayload = {
        ...state.tripsPayload,
        tripTasks: prepareTripTasks([
          ...tripTasks.filter(
            (task) =>
              !(
                task.type === 'DROP_TRAILER' &&
                task.trailerId === trailerId &&
                task.orderEventId == null &&
                task.sourceOrderPkId == null &&
                !task.orderId
              )
          ),
          {
            address,
            estimatedTime: '',
            name: '',
            orderEventId: undefined,
            orderId,
            sourceOrderPkId,
            sequenceId,
            startTime: '',
            tractorId,
            trailerId,
            type: 'DROP_TRAILER' as const,
          },
        ]),
      }
    },
    addHookTrailerTask: (
      state,
      action: PayloadAction<{
        address: string
        orderId: string
        sourceOrderPkId: number
        deliveryOrderEventId: number
      }>
    ) => {
      const {
        address,
        orderId,
        sourceOrderPkId,
        deliveryOrderEventId,
      } = action.payload
      const deliveryTask = state.tripsPayload.tripTasks.find(
        (task) => task.orderEventId === deliveryOrderEventId
      )
      const { tractorId, trailerId: currentTrailerId } = getEquipmentHookIds(
        state.tripsPayload.tripTasks
      )
      const trailerId = deliveryTask?.trailerId ?? currentTrailerId

      state.tripsPayload.tripTasks = prepareTripTasks([
        ...state.tripsPayload.tripTasks.filter(
          (task) =>
            !(
              task.type === 'HOOK_TRAILER' &&
              task.trailerId === trailerId &&
              task.orderEventId == null &&
              task.sourceOrderPkId == null &&
              !task.orderId
            )
        ),
        {
          address,
          estimatedTime: '',
          name: '',
          orderEventId: undefined,
          orderId,
          sourceOrderPkId,
          sequenceId: (deliveryTask?.sequenceId ?? 1) - 1,
          startTime: '',
          tractorId,
          trailerId,
          type: 'HOOK_TRAILER' as const,
        },
      ])
    },
    removeEnteredTrailerTask: (
      state,
      action: PayloadAction<{
        type: TripPayload['type']
        sourceOrderPkId?: number
        orderId?: string
      }>
    ) => {
      state.tripsPayload.tripTasks = prepareTripTasks(
        state.tripsPayload.tripTasks.filter((task) => {
          if (
            task.type !== action.payload.type ||
            task.orderEventId != null
          ) {
            return true
          }

          if (action.payload.sourceOrderPkId != null) {
            return task.sourceOrderPkId !== action.payload.sourceOrderPkId
          }

          return task.orderId !== action.payload.orderId
        })
      )
    },
    addHookEquipment: (
      state,
      action: PayloadAction<{ equipmentType: EquipmentType; address: string }>
    ) => {
      state.tripsPayload = {
        ...state.tripsPayload,
        tripTasks: prepareTripTasks([
          ...state.tripsPayload.tripTasks,
          {
            address: action.payload.address,
            estimatedTime: '',
            name: '',
            // orderEventId: getMaxOrderEventId(state.tripsPayload.tripTasks) + 10,
            orderEventId: undefined,
            sequenceId: getMaxSequenceId(state.tripsPayload.tripTasks) + 10,
            startTime: '',
            tractorId: null,
            trailerId: null,
            type:
              action.payload.equipmentType === 'Tractor'
                ? 'HOOK_TRACTOR'
                : 'HOOK_TRAILER',
          },
        ]),
      }
    },
    addDropEquipment: (
      state,
      action: PayloadAction<{ equipmentType: EquipmentType; address: string }>
    ) => {
      state.tripsPayload = {
        ...state.tripsPayload,
        tripTasks: prepareTripTasks([
          ...state.tripsPayload.tripTasks,
          {
            address: action.payload.address,
            estimatedTime: '',
            name: '',
            // orderEventId: getMaxOrderEventId(state.tripsPayload.tripTasks) + 10,
            orderEventId: undefined,
            sequenceId: getMaxSequenceId(state.tripsPayload.tripTasks) + 10,
            startTime: '',
            tractorId: null,
            trailerId: null,
            type:
              action.payload.equipmentType === 'Tractor'
                ? 'DROP_TRACTOR'
                : 'DROP_TRAILER',
          },
        ]),
      }
    },
    selectOrderId: (state, action: PayloadAction<{ orderId: number }>) => {
      state.orderId = action.payload.orderId
    },
    setOrderEventId: (
      state,
      action: PayloadAction<{ orderEventId: number }>
    ) => {
      state.orderEventId = action.payload.orderEventId
    },
    trackSessionCreatedOrderEvent: (
      state,
      action: PayloadAction<SessionCreatedOrderEvent>
    ) => {
      const { orderId, orderEventId } = action.payload
      if (
        state.sessionCreatedOrderEvents.some(
          (event) => event.orderEventId === orderEventId
        )
      ) {
        return
      }
      state.sessionCreatedOrderEvents.push({ orderId, orderEventId })
    },
    untrackSessionCreatedOrderEvent: (
      state,
      action: PayloadAction<{ orderEventId: number }>
    ) => {
      state.sessionCreatedOrderEvents =
        state.sessionCreatedOrderEvents.filter(
          (event) => event.orderEventId !== action.payload.orderEventId
        )
    },
    clearTripPayload: (state) => {
      state.tripsPayload = initialState.tripsPayload
      state.editTripOrderEventIds = []
      state.sessionCreatedOrderEvents = []
      state.firstDriver = DriverFixture
      state.secondDriver = DriverFixture
    },
    replaceTripTasks: (
      state,
      action: PayloadAction<{ tripTasks: TripPayload[] }>
    ) => {
      state.tripsPayload.tripTasks = prepareTripTasks(action.payload.tripTasks)
    },
    updateEquipmentTaskAddress: (
      state,
      action: PayloadAction<{ sequenceId: number; address: string }>
    ) => {
      const task = state.tripsPayload.tripTasks.find(
        (tripTask) => tripTask.sequenceId === action.payload.sequenceId
      )

      if (task) {
        task.address = action.payload.address
      }
    },
    loadTripForEdit: (state, action: PayloadAction<{ trip: TripResponse }>) => {
      const tripPayload = tripResponseToCreatePayload(action.payload.trip)
      state.tripsPayload = {
        ...tripPayload,
        tripTasks: prepareTripTasks(tripPayload.tripTasks),
      }
      state.editTripOrderEventIds = action.payload.trip.tripTasks
        .map((task) => task.orderEvent?.orderEventId)
        .filter((id): id is number => id != null)
    },
    makeError: (state, action: PayloadAction<{ error: TripsError }>) => {
      state.error = action.payload.error
    },
    clearError: (state) => {
      state.error = {
        open: false,
        message: '',
        variant: 'info',
      }
    },
    setSelectedTrip: (state, action: PayloadAction<{ trip: TripResponse }>) => {
      state.selectedTrip = action.payload.trip
    },
    clearSelectedTrip: (state) => {
      state.selectedTrip = undefined
    },
    setTripCreateCompletion: (
      state,
      action: PayloadAction<{ isComplete: boolean; driverName: string }>
    ) => {
      state.tripCreateCompletion = action.payload
    },
  },
})

export const {
  addFirstDriver,
  addSecondDriver,
  addDrivers,
  addEquipments,
  removeTractor,
  removeTrailer,
  removeTrailerById,
  replaceTrailerById,
  removeTaskById,
  removeOrderTasks,
  addDropTrailerTask,
  addHookTrailerTask,
  removeEnteredTrailerTask,
  addDropEquipment,
  updateEquipmentTaskAddress,
  selectOrderId,
  setOrderEventId,
  trackSessionCreatedOrderEvent,
  untrackSessionCreatedOrderEvent,
  clearTripPayload,
  replaceTripTasks,
  loadTripForEdit,
  makeError,
  clearError,
  setSelectedTrip,
  clearSelectedTrip,
  setTripCreateCompletion,
} = tripsSlice.actions

export default tripsSlice.reducer
